/* eslint-disable max-lines */
import React, { FC, useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { useMapContext } from "@evergis/react";
import { GeometryType } from "@evergis/api";
import { Coordinates } from "@evergis/sgis/es/baseTypes";
import { Divider, IconButton, IconToggle, Popover, Tooltip } from "@evergis/ui";

import { GeometryInfo } from "./GeometryInfo";
import { Settings } from "./Settings";
import { Selection } from "./Selection";
import { BufferRadiusInput } from "./BufferRadiusInput";
import { MapControlTool } from "../../styled";

import {
  CollapsableContainer,
  DropdownIcon,
  ExpanderContainer,
  GeometryResetControl,
  GeometrySelectionWrapper,
  HideableArea,
  SelectionControlContainer,
  SelectionMenuContainer,
  SelectionMenuItem,
} from "./styled";

import { POLYGON_LIKE_TOOLS } from "./constants";

import { ShadowedPointSymbol } from "../../../../react-sgis/symbols/editorSymbols/ShadowedPointSymbol";
import { ShadowedPolySymbol } from "../../../../react-sgis/symbols/editorSymbols/ShadowedPolySymbol";

import {
  CalculatedFeatureTypes,
  clearGeometrySelectionHistory,
} from "../../../../ducks/geometrySelection/geometrySelection";

import { GeometrySelectionSymbolType, useSelectionSymbols } from "./Selection/hooks/useSelectionSymbols";
import { createPointFeature } from "./Selection/utils/createPointFeature";
import { createPolygonFeature } from "./Selection/utils/createPolygonFeature";
import { createPolylineFeature } from "./Selection/utils/createPolylineFeature";
import { toggleMeasurer } from "../../../../ducks/measurer/measurer";
import { useGeometrySelectionContext } from "../../../../hooks/geometrySelection/useGeometrySelectionContext";
import { useGeometrySelection } from "../../../../hooks/geometrySelection/useGeometrySelection";
import { useToggle } from "../../../../hooks/useToggle";
import { useNirvanaMode } from "../../../../hooks/layout/useNirvanaMode";
import { useProjectName } from "../../../../hooks/project/useProjectName";
import { useEditCoordinatesDialog } from "../../../../hooks/useDialogs";
import { useFeatureCard } from "../../../FeatureCard/hooks/useFeatureCard";

import { GeometrySelectionToolTypes, GeometrySelectionTypes } from "./types";

const SELECTION_OPTIONS = [
  { type: GeometrySelectionTypes.Point, localizationKey: "point" },
  { type: GeometrySelectionTypes.Line, localizationKey: "line" },
  { type: GeometrySelectionTypes.Circle, localizationKey: "circle" },
  { type: GeometrySelectionTypes.Polygon, localizationKey: "polygon" },
  { type: GeometrySelectionTypes.Rect, localizationKey: "rectangle" },
  { type: GeometrySelectionTypes.Coordinates, localizationKey: "coordinates" },
  { type: GeometrySelectionTypes.Route, localizationKey: "route" },
];

// eslint-disable-next-line max-lines-per-function
export const GeometrySelectionTools: FC = () => {
  const { t } = useTranslation("mapControls");
  const dispatch = useDispatch();
  const { map } = useMapContext();
  const { isNirvanaMode } = useNirvanaMode();
  const [infoOpen, toggleInfo] = useToggle(false);
  const [settingsOpen, toggleSettings] = useToggle(false);
  const [geometrySelection, setGeometrySelection] = useGeometrySelection();
  const { currentTool, active, type, history, isBuffer, editHistory } = geometrySelection;
  const { stepBack, stepForward, purgeFeatures, setFeatures, saveEditState } = useGeometrySelectionContext();
  const [typeMenuOpen, toggleTypeMenuOpen] = useToggle(false);
  const openEditCoordinatesDialog = useEditCoordinatesDialog();
  const symbols = useSelectionSymbols();
  const [collapsed, setCollapsed] = useState<boolean>(true);
  const projectName = useProjectName();
  const [, { closeCard }] = useFeatureCard();

  const onCreateFinish = useCallback(
    (coordinates: Coordinates | Coordinates[][] | null, geometryType: GeometryType) => {
      let processedFeatures;
      let selectionType;

      switch (geometryType) {
        case GeometryType.Point:
          processedFeatures = createPointFeature(
            coordinates as Coordinates,
            map.crs,
            symbols[GeometrySelectionSymbolType.point] as ShadowedPointSymbol,
          );

          selectionType = GeometrySelectionTypes.Point;
          break;
        case GeometryType.Polygon:
          processedFeatures = coordinates.map(rings =>
            createPolygonFeature(
              rings,
              map.crs,
              symbols[GeometrySelectionSymbolType.polygonCreate] as ShadowedPolySymbol,
            ),
          );

          selectionType = GeometrySelectionTypes.Polygon;
          break;
        case GeometryType.Polyline:
          processedFeatures = coordinates.map(contour =>
            createPolylineFeature(
              [contour],
              map.crs,
              map.resolution,
              symbols[GeometrySelectionSymbolType.polygonEdit] as ShadowedPolySymbol,
              symbols[GeometrySelectionSymbolType.polygonCreate] as ShadowedPolySymbol,
              symbols[GeometrySelectionSymbolType.polylineEdit] as ShadowedPolySymbol,
              symbols[GeometrySelectionSymbolType.polylineCreate] as ShadowedPolySymbol,
            ),
          );

          selectionType = GeometrySelectionTypes.Line;
          break;
        default:
          return;
      }

      purgeFeatures?.();

      setGeometrySelection({
        active: true,
        type: selectionType,
        currentTool: ![GeometrySelectionTypes.Coordinates].includes(selectionType)
          ? GeometrySelectionToolTypes.Replace
          : undefined,
        history: {
          features: [],
          step: -1,
        },
      });

      setFeatures(processedFeatures);
    },
    [purgeFeatures, setGeometrySelection, setFeatures, map.crs, map.resolution, symbols],
  );

  const onMenuItemSelect = useCallback(
    (selectionType: GeometrySelectionTypes) => {
      const isSwitchingPolygons =
        POLYGON_LIKE_TOOLS.includes(selectionType) && POLYGON_LIKE_TOOLS.includes(geometrySelection.type);

      if (selectionType === GeometrySelectionTypes.Coordinates) {
        openEditCoordinatesDialog([], {
          editGeometry: onCreateFinish,
          crs: map.crs,
        });

        return;
      }

      toggleTypeMenuOpen();

      if (
        selectionType !== geometrySelection.type &&
        !isSwitchingPolygons &&
        geometrySelection.active &&
        geometrySelection.history.features.length > 0
      ) {
        purgeFeatures?.();
        closeCard();

        setGeometrySelection({
          active: true,
          type: selectionType,
          currentTool: ![GeometrySelectionTypes.Coordinates].includes(selectionType)
            ? GeometrySelectionToolTypes.Replace
            : undefined,
          history: {
            features: [],
            step: -1,
          },
        });
      } else {
        setGeometrySelection({
          ...geometrySelection,
          active: true,
          type: selectionType,
          currentTool: isSwitchingPolygons
            ? currentTool
            : ![GeometrySelectionTypes.Coordinates].includes(selectionType)
            ? GeometrySelectionToolTypes.Replace
            : undefined,
        });
      }
    },
    [
      geometrySelection,
      openEditCoordinatesDialog,
      onCreateFinish,
      map.crs,
      toggleTypeMenuOpen,
      purgeFeatures,
      setGeometrySelection,
      currentTool,
      closeCard,
    ],
  );

  const onPickTool = useCallback(
    (tool: GeometrySelectionToolTypes) => {
      setGeometrySelection({
        currentTool: tool,
      });
    },
    [setGeometrySelection],
  );

  const onToggleBuffer = useCallback(() => {
    setGeometrySelection({
      isBuffer: !isBuffer,
    });
  }, [setGeometrySelection, isBuffer]);

  const onClearSelection = useCallback(() => {
    purgeFeatures?.();
    dispatch(clearGeometrySelectionHistory());
    closeCard();
  }, [closeCard, dispatch, purgeFeatures]);

  const onEditStart = useCallback(() => {
    setGeometrySelection({
      currentTool: GeometrySelectionToolTypes.Edit,
      editHistory: {
        features: [],
        step: -1,
      },
    });
  }, [setGeometrySelection]);

  const onDismissChanges = useCallback(() => {
    setGeometrySelection({
      currentTool: GeometrySelectionToolTypes.Replace,
      editHistory: {
        features: [],
        step: -1,
      },
    });
  }, [setGeometrySelection]);

  const onSaveChanges = useCallback(() => {
    setGeometrySelection({
      currentTool: GeometrySelectionToolTypes.Replace,
    });

    saveEditState();
  }, [setGeometrySelection, saveEditState]);

  const prevIsNirvanaMode = useRef(isNirvanaMode);

  useEffect(() => {
    if (active) {
      dispatch(toggleMeasurer(false));
    }
  }, [active]);

  useEffect(() => {
    if (projectName) {
      purgeFeatures?.();
      dispatch(clearGeometrySelectionHistory());
    }
  }, [projectName]);

  useEffect(() => {
    if (typeMenuOpen && prevIsNirvanaMode.current !== isNirvanaMode) {
      toggleTypeMenuOpen();
    }
    prevIsNirvanaMode.current = isNirvanaMode;
  }, [isNirvanaMode]);

  if (!active) {
    return null;
  }

  return (
    <>
      <GeometrySelectionWrapper>
        <MapControlTool>
          <CollapsableContainer collapsed={collapsed} $width={!!history.features.length && "6.75rem"}>
            <SelectionControlContainer withPadding active={typeMenuOpen}>
              <IconToggle
                kind={geometrySelection.type}
                isSelected={geometrySelection.active}
                onClick={toggleTypeMenuOpen}
              />
              <Popover
                zIndex={101}
                anchor={<DropdownIcon kind={"input_dropdown"} onClick={toggleTypeMenuOpen} />}
                open={typeMenuOpen}
                onRequestClose={toggleTypeMenuOpen}
              >
                <SelectionMenuContainer>
                  {SELECTION_OPTIONS.map(option => (
                    <SelectionMenuItem key={option.type}>
                      <Tooltip content={t(option.localizationKey)} placement="right" arrow delay={[600, 0]}>
                        {ref => (
                          <IconToggle
                            innerRef={ref}
                            kind={option.type}
                            isSelected={active && type === option.type}
                            onClick={() => onMenuItemSelect(option.type)}
                          />
                        )}
                      </Tooltip>
                    </SelectionMenuItem>
                  ))}
                </SelectionMenuContainer>
              </Popover>
            </SelectionControlContainer>
            <HideableArea isHidden={collapsed}>
              <Divider vertical />
              {type === GeometrySelectionTypes.Route ? (
                <>
                  <Tooltip content={t("walking")} placement="right" arrow delay={[600, 0]}>
                    {ref => (
                      <IconToggle
                        innerRef={ref}
                        kind={"pedestrian"}
                        isSelected={geometrySelection.settings.routeType === CalculatedFeatureTypes.Walking}
                        onClick={() => {
                          setGeometrySelection({
                            settings: {
                              ...geometrySelection.settings,
                              routeType: CalculatedFeatureTypes.Walking,
                            },
                          });
                        }}
                      />
                    )}
                  </Tooltip>
                  <Tooltip content={t("car")} placement="right" arrow delay={[600, 0]}>
                    {ref => (
                      <IconToggle
                        innerRef={ref}
                        kind={"car"}
                        isSelected={geometrySelection.settings.routeType === CalculatedFeatureTypes.Car}
                        onClick={() => {
                          setGeometrySelection({
                            settings: {
                              ...geometrySelection.settings,
                              routeType: CalculatedFeatureTypes.Car,
                            },
                          });
                        }}
                      />
                    )}
                  </Tooltip>
                </>
              ) : (
                <>
                  <Tooltip content={t("replaceArea")} placement="right" arrow delay={[600, 0]}>
                    {ref => (
                      <IconToggle
                        innerRef={ref}
                        kind={"change_geom"}
                        isSelected={currentTool === GeometrySelectionToolTypes.Replace}
                        onClick={() => onPickTool(GeometrySelectionToolTypes.Replace)}
                      />
                    )}
                  </Tooltip>
                  <Tooltip content={t("uniteArea")} placement="right" arrow delay={[600, 0]}>
                    {ref => (
                      <IconToggle
                        innerRef={ref}
                        kind={"geometryUnite"}
                        isSelected={currentTool === GeometrySelectionToolTypes.Unite}
                        onClick={() => onPickTool(GeometrySelectionToolTypes.Unite)}
                      />
                    )}
                  </Tooltip>
                  {POLYGON_LIKE_TOOLS.includes(type) && (
                    <Tooltip content={t("excludeArea")} placement="right" arrow delay={[600, 0]}>
                      {ref => (
                        <IconToggle
                          innerRef={ref}
                          kind={"geometryCrop"}
                          isSelected={currentTool === GeometrySelectionToolTypes.Crop}
                          onClick={() => onPickTool(GeometrySelectionToolTypes.Crop)}
                        />
                      )}
                    </Tooltip>
                  )}
                </>
              )}
              <Divider vertical />
              <Tooltip content={t("createBufferZone")} placement="right" arrow delay={[600, 0]}>
                {ref => (
                  <IconToggle innerRef={ref} kind={"tools_buffer"} isSelected={isBuffer} onClick={onToggleBuffer} />
                )}
              </Tooltip>
              {isBuffer && <BufferRadiusInput />}
              <Divider vertical />
              <Tooltip content={t("back")} placement="right" arrow delay={[600, 0]}>
                {ref => (
                  <IconButton
                    innerRef={ref}
                    kind={"step_back"}
                    disabled={currentTool !== GeometrySelectionToolTypes.Edit ? history.step < 1 : editHistory.step < 1}
                    onClick={stepBack}
                  />
                )}
              </Tooltip>
              <Tooltip content={t("forward")} placement="right" arrow delay={[600, 0]}>
                {ref => (
                  <IconButton
                    innerRef={ref}
                    kind={"step_forward"}
                    disabled={
                      currentTool !== GeometrySelectionToolTypes.Edit
                        ? history.step === history.features.length - 1
                        : editHistory.step === editHistory.features.length - 1
                    }
                    onClick={stepForward}
                  />
                )}
              </Tooltip>
              <Divider vertical />
              {![GeometrySelectionTypes.Point, GeometrySelectionTypes.Coordinates].includes(type) && (
                <Tooltip content={t("info")} placement="right" arrow delay={[600, 0]}>
                  {ref => (
                    <Popover
                      zIndex={101}
                      anchor={
                        <IconToggle
                          innerRef={ref}
                          kind={"info"}
                          disabled={history.step === -1}
                          isSelected={infoOpen}
                          onClick={toggleInfo}
                        />
                      }
                      open={infoOpen}
                      onRequestClose={toggleInfo}
                    >
                      <GeometryInfo />
                    </Popover>
                  )}
                </Tooltip>
              )}
              <Tooltip content={t("selectionSettings")} placement="right" arrow delay={[600, 0]}>
                {ref => (
                  <Popover
                    zIndex={101}
                    anchor={
                      <IconToggle
                        innerRef={ref}
                        kind={"settings_horizontal"}
                        isSelected={settingsOpen}
                        onClick={toggleSettings}
                      />
                    }
                    open={settingsOpen}
                    onRequestClose={toggleSettings}
                  >
                    <Settings />
                  </Popover>
                )}
              </Tooltip>
              {currentTool !== GeometrySelectionToolTypes.Edit && type !== GeometrySelectionTypes.Point && (
                <>
                  <Divider vertical />
                  <Tooltip content={t("selectionEditing")} placement="right" arrow delay={[600, 0]}>
                    {ref => (
                      <IconButton
                        innerRef={ref}
                        kind={"edit"}
                        disabled={history.features.length === 0}
                        onClick={onEditStart}
                      />
                    )}
                  </Tooltip>
                </>
              )}
              {currentTool === GeometrySelectionToolTypes.Edit && (
                <>
                  <Divider vertical />
                  <Tooltip
                    content={t("panelHeader.dismissChanges", { ns: "common" })}
                    placement="right"
                    arrow
                    delay={[600, 0]}
                  >
                    {ref => <IconButton innerRef={ref} kind={"close"} onClick={onDismissChanges} error />}
                  </Tooltip>
                  <Tooltip
                    content={t("panelHeader.saveChanges", { ns: "common" })}
                    placement="right"
                    arrow
                    delay={[600, 0]}
                  >
                    {ref => <IconButton innerRef={ref} kind={"success"} onClick={onSaveChanges} primary />}
                  </Tooltip>
                </>
              )}
            </HideableArea>
            {!!history.features.length && (
              <GeometryResetControl>
                <Tooltip content={t("reset")} placement="right" arrow delay={[600, 0]}>
                  {ref => <IconButton innerRef={ref} kind={"clear"} onClick={onClearSelection} />}
                </Tooltip>
              </GeometryResetControl>
            )}
          </CollapsableContainer>
          <ExpanderContainer>
            <Divider vertical />
            <IconButton
              kind={collapsed ? "next" : "prev"}
              disabled={currentTool === GeometrySelectionToolTypes.Edit}
              onClick={() => setCollapsed(!collapsed)}
            />
          </ExpanderContainer>
        </MapControlTool>
      </GeometrySelectionWrapper>
      <Selection />
    </>
  );
};
